// Libs
import classNames from 'classnames/bind';
import { Box, Button, Menu, Stack, Typography } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { IFilterValueChange } from '~/utils/interface/common';
// Styles, images, icons
import styles from './BaseFilters.module.scss';
import { icons } from '~/assets';

type ChildrenActions<T> = {
  isChecked?: { [K in keyof T]?: boolean };
  value?: T | undefined;
  onChange?: (data: IFilterValueChange<T>) => void;
  onCheckboxChange: (key: keyof T, checked: boolean) => void;
};

type Props<T> = {
  value?: T;
  defaultValue?: T;
  children?: (props: ChildrenActions<T>) => ReactNode;
  onApply?: (value: T | undefined) => void;
};

const cx = classNames.bind(styles);

const BaseFilters = <T,>(props: Props<T>) => {
  //#region Destructuring Props
  const { value, children, defaultValue, onApply } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDisableApply, setIsDisableApply] = useState<boolean>(false);
  const [rootValueFilter, setRootValueFilter] = useState<T | undefined>(defaultValue);
  const [tempValueFilter, setTempValueFilter] = useState<T | undefined>(defaultValue);
  const [isChecked, setIsChecked] = useState<{ [K in keyof T]?: boolean }>({});
  const [tempIsChecked, setTempIsChecked] = useState<{ [K in keyof T]?: boolean }>({});
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!value) return;

    setTempValueFilter(value);

    const newChecked = Object.keys(value).reduce((acc, key) => {
      acc[key as keyof T] = !!value[key as keyof T];
      return acc;
    }, {} as { [K in keyof T]?: boolean });

    setIsChecked(newChecked);
    setTempIsChecked(newChecked);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!value) return;

    const isDisableBtnApply = JSON.stringify(tempValueFilter) === JSON.stringify(rootValueFilter);

    setIsDisableApply(isDisableBtnApply);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempValueFilter, rootValueFilter]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setTempValueFilter(rootValueFilter);
    setTempIsChecked(isChecked);
  };

  const handleValueFilterChange = ({ name, value }: IFilterValueChange<T>) => {
    setTempValueFilter((prev) => ({ ...(prev || {}), [name]: value } as T));
  };

  const handleApplyFilter = () => {
    setRootValueFilter(tempValueFilter);
    onApply && onApply(tempValueFilter);
    setIsChecked(tempIsChecked);
    setAnchorEl(null);
  };

  const onCheckboxChange = (key: keyof T, checked: boolean) => {
    setTempIsChecked((prev) => ({ ...prev, [key]: checked }));

    setTempValueFilter((prev) => {
      if (!prev) return prev;

      const updateValue = { ...prev };

      if (!checked) {
        delete updateValue[key];
      }

      return updateValue as T;
    });
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='baseFiltersComponent' className={cx('container')}>
      <Button
        variant='outlined'
        color='secondary'
        className={cx('btnFilter')}
        startIcon={<img src={icons.filterIconDefault} alt={t('common_alt_img')} />}
        onClick={handleShowFilter}
      >
        {t('common_action_filter')}
      </Button>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseFilter}
        slotProps={{
          paper: {
            className: cx('paper'),
          },
        }}
        MenuListProps={{ className: cx('menuList') }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoFocus={false}
      >
        <Stack gap={'12px'}>
          {children ? (
            children({
              isChecked: tempIsChecked,
              value: tempValueFilter,
              onChange: handleValueFilterChange,
              onCheckboxChange: onCheckboxChange,
            })
          ) : (
            <Typography align='center' className={cx('noData')}>
              {t('common_no_data_available')}
            </Typography>
          )}

          <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} gap={'12px'}>
            <Button variant='outlined' color='secondary' onClick={handleCloseFilter}>
              {t('common_action_cancel')}
            </Button>

            <Button variant='contained' disabled={isDisableApply} onClick={handleApplyFilter}>
              {t('common_action_apply')}
            </Button>
          </Stack>
        </Stack>
      </Menu>
    </Box>
  );
};

export default BaseFilters;
